import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Badge from '../../components/common/Badge/Badge';
import ClearFiltersButton from '../../components/common/ClearFiltersButton/ClearFiltersButton';
import DataTable from '../../components/common/DataTable/DataTable';
import DatePresetFilter from '../../components/common/DatePresetFilter/DatePresetFilter';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import FilterBar from '../../components/common/FilterBar/FilterBar';
import FilterChipsBar from '../../components/common/FilterChipsBar/FilterChipsBar';
import {
  buildDateRangeFilterChip,
  buildOptionFilterChips,
  compactFilterChips,
} from '../../components/common/FilterChipsBar/buildFilterChips';
import FilterGroup from '../../components/common/FilterGroup/FilterGroup';
import Loading from '../../components/common/Loading/Loading';
import MultiSelectFilter from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import PageContainer, { PageBody } from '../../components/common/PageContainer/PageContainer';
import PageSizeSelect from '../../components/common/PageSizeSelect/PageSizeSelect';
import Pagination from '../../components/common/Pagination/Pagination';
import ClassificationBadge from '../../components/common/ClassificationBadge/ClassificationBadge';
import ProjectBadge from '../../components/common/ProjectBadge/ProjectBadge';
import ReactivatedBadge from '../../components/common/ReactivatedBadge/ReactivatedBadge';
import SensitiveStudentBadge from '../../components/common/SensitiveStudentBadge/SensitiveStudentBadge';
import PregnantStudentBadge from '../../components/common/PregnantStudentBadge/PregnantStudentBadge';
import RedFlagBadge from '../../components/common/RedFlagBadge/RedFlagBadge';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import StatusIndicator from '../../components/common/StatusIndicator';
import {
  COMMERCIAL_JOINED_AT_DATE_PRESETS,
  detectDatePreset,
  formatDatePresetShort,
} from '../../constants/datePresets';
import { useEarlyCheckins } from '../../hooks/useEarlyCheckins';
import { useMarketingProjects } from '../../hooks/useMarketingProjects';
import { usePageSize } from '../../hooks/usePageSize';
import { queryFilter, useQueryFilters } from '../../hooks/useQueryFilters';
import type { EarlyCheckinStage, EarlyCheckinStatus } from '../../types/earlyCheckin';
import {
  EARLY_CHECKIN_STAGE_LABELS,
  EARLY_CHECKIN_STAGE_OPTIONS,
  EARLY_CHECKIN_STAGE_VARIANTS,
} from '../../types/earlyCheckin';
import {
  getStudentClassificationBadgeColors,
  shouldDisplayStudentClassificationBadge,
} from '../../types/studentClassification';
import {
  getEarlyCheckinSuccessLabel,
  isEarlyCheckinStage,
  isEarlyCheckinSuccessful,
} from '../../utils/earlyCheckin';
import { formatDate } from '../../utils';
import orcStyles from './Orchestrator.module.css';
import styles from './OrchestratorFup.module.css';

const DATE_FILTER_PLACEHOLDER = 'Data';

const EarlyCheckinSuccessCell: React.FC<{
  kind: 'day_8' | 'day_15';
  status: EarlyCheckinStatus | null;
}> = ({ kind, status }) => {
  const success = isEarlyCheckinSuccessful(status);

  return (
    <div className={styles.checkpointCell}>
      <StatusIndicator
        active={success}
        label={getEarlyCheckinSuccessLabel(kind, status)}
      />
    </div>
  );
};

const ORCHESTRATOR_FUP_QUERY_SCHEMA = {
  page: queryFilter.integer(1, { min: 1 }),
  search: queryFilter.string(),
  statuses: queryFilter.array<EarlyCheckinStage>([]),
  projectCodes: queryFilter.array<string>([]),
  joinedAtStart: queryFilter.string(),
  joinedAtEnd: queryFilter.string(),
  sortOrder: queryFilter.enum<'asc' | 'desc'>('asc', ['asc', 'desc']),
};

function formatJoinedAtChipLabel(start: string, end: string): string {
  const preset = detectDatePreset(start, end, COMMERCIAL_JOINED_AT_DATE_PRESETS);
  const presetOption = COMMERCIAL_JOINED_AT_DATE_PRESETS.find((option) => option.value === preset);
  const selectionLabel = presetOption && preset !== 'custom'
    ? presetOption.label
    : `${formatDatePresetShort(start)} – ${formatDatePresetShort(end)}`;

  return `${DATE_FILTER_PLACEHOLDER}: ${selectionLabel}`;
}

const OrchestratorFup: React.FC = () => {
  const navigate = useNavigate();
  const {
    filters,
    setFilter,
    setFilters,
  } = useQueryFilters(ORCHESTRATOR_FUP_QUERY_SCHEMA, { pageKey: 'page' });
  const {
    page,
    pageSize,
    pageSizeOptions,
    handlePageChange,
    handlePageSizeChange,
  } = usePageSize('orchestrator_fup_page_size');
  const {
    rows,
    pagination,
    loading,
    error,
  } = useEarlyCheckins(page, pageSize, {
    search: filters.search,
    projectCodes: filters.projectCodes,
    stages: filters.statuses,
    sortOrder: filters.sortOrder,
    joinedAtStart: filters.joinedAtStart || undefined,
    joinedAtEnd: filters.joinedAtEnd || undefined,
  });
  const projects = useMarketingProjects();

  const projectFilterOptions = useMemo(
    () => projects.map((project) => ({
      value: project.code,
      label: project.code,
      color: project.colors.background,
    })),
    [projects],
  );

  const statusOptions = useMemo(
    () => EARLY_CHECKIN_STAGE_OPTIONS.map((option) => ({
      value: option.value,
      label: option.label,
    })),
    [],
  );

  useEffect(() => {
    if (loading) {
      return;
    }

    if (page > 1 && pagination.totalRecords === 0) {
      setFilter('page', 1, { resetPage: false });
      return;
    }

    if (pagination.totalPages > 0 && page > pagination.totalPages) {
      setFilter('page', pagination.totalPages, { resetPage: false });
    }
  }, [loading, page, pagination.totalPages, pagination.totalRecords, setFilter]);

  const hasActiveFilters = Boolean(
    filters.search
    || filters.statuses.length > 0
    || filters.projectCodes.length > 0
    || (filters.joinedAtStart && filters.joinedAtEnd)
    || filters.sortOrder !== 'asc',
  );

  const chips = useMemo(() => compactFilterChips(
    buildOptionFilterChips({
      filterKey: 'statuses',
      values: filters.statuses,
      options: statusOptions,
      labelPrefix: 'Status',
      onRemove: (value) => setFilter('statuses', (current) => current.filter((item) => item !== value)),
    }),
    buildOptionFilterChips({
      filterKey: 'projectCodes',
      values: filters.projectCodes,
      options: projectFilterOptions,
      labelPrefix: 'Projeto',
      onRemove: (value) => setFilter(
        'projectCodes',
        (current) => current.filter((item) => item !== value),
      ),
    }),
    buildDateRangeFilterChip({
      filterKey: 'joinedAt',
      start: filters.joinedAtStart,
      end: filters.joinedAtEnd,
      label: formatJoinedAtChipLabel(filters.joinedAtStart, filters.joinedAtEnd),
      onRemove: () => setFilters({ joinedAtStart: '', joinedAtEnd: '' }),
    }),
  ), [
    filters.joinedAtEnd,
    filters.joinedAtStart,
    filters.projectCodes,
    filters.statuses,
    projectFilterOptions,
    setFilter,
    setFilters,
    statusOptions,
  ]);

  const handleJoinedAtChange = useCallback((start: string, end: string) => {
    setFilters({ joinedAtStart: start, joinedAtEnd: end });
  }, [setFilters]);

  const handleClearFilters = () => {
    setFilters({
      search: '',
      statuses: [],
      projectCodes: [],
      joinedAtStart: '',
      joinedAtEnd: '',
      sortOrder: 'asc',
    });
  };

  return (
    <PageContainer>
      <FilterBar gap="sm" pushFirstChild>
        <RecordCount
          label="Alunas:"
          count={`${pagination.totalRecords} alunas`}
          className={orcStyles['student-count']}
        />

        <FilterGroup>
          <SearchFilterBar
            searchValue={filters.search}
            onSearchChange={(value) => setFilter('search', value)}
            searchPlaceholder="Pesquisar aluna"
          />
        </FilterGroup>

        {projectFilterOptions.length > 0 && (
          <FilterGroup>
            <MultiSelectFilter
              values={filters.projectCodes}
              onChange={(values) => setFilter('projectCodes', values)}
              options={projectFilterOptions}
              placeholder="Projeto"
              searchPlaceholder="Pesquisar"
              noResultsText="Nenhum projeto encontrado"
              clearButtonLabel="Limpar filtro"
              hideChips
              showSelectionCount={false}
            />
          </FilterGroup>
        )}

        <FilterGroup>
          <MultiSelectFilter
            values={filters.statuses}
            onChange={(values) => setFilter('statuses', values.filter(isEarlyCheckinStage))}
            options={statusOptions}
            placeholder="Estado do FUP"
            searchPlaceholder="Pesquisar"
            noResultsText="Nenhum estado encontrado"
            clearButtonLabel="Limpar filtro"
            hideChips
            showSelectionCount={false}
          />
        </FilterGroup>

        <FilterGroup>
          <DatePresetFilter
            dateStart={filters.joinedAtStart}
            dateEnd={filters.joinedAtEnd}
            onChange={handleJoinedAtChange}
            placeholder={DATE_FILTER_PLACEHOLDER}
            presetOptions={COMMERCIAL_JOINED_AT_DATE_PRESETS}
            allowEmpty
            keepPlaceholder
          />
        </FilterGroup>

        {hasActiveFilters && (
          <ClearFiltersButton onClick={handleClearFilters} />
        )}
      </FilterBar>

      <FilterChipsBar chips={chips} />

      <PageBody>
        {loading ? (
          <Loading />
        ) : error ? (
          <EmptyState message={`Erro ao carregar o Orquestrador FUP: ${error}`} />
        ) : pagination.totalRecords === 0 ? (
          <EmptyState message="Nenhuma aluna encontrada com os filtros selecionados." />
        ) : (
          <>
            <DataTable
              className={styles.table}
              headers={[
                <span
                  key="nome"
                  className={orcStyles['sortable-header']}
                  onClick={() => setFilter(
                    'sortOrder',
                    (currentSortOrder) => (currentSortOrder === 'asc' ? 'desc' : 'asc'),
                  )}
                >
                  Nome
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                    {filters.sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                  </span>
                </span>,
                'Tags',
                'Data de adesão',
                '8 dias',
                '15 dias',
                'Estado do FUP',
              ]}
              gridTemplate="minmax(220px, 1.6fr) minmax(180px, 1.1fr) 140px 96px 96px 170px"
              highlightTerm={filters.search}
            >
              {rows.map((row) => (
                <DataTable.Row key={row.student_id} sensitive={Boolean(row.is_sensitive)}>
                  <div className={orcStyles['col-name']}>
                    <span
                      className={orcStyles['student-name']}
                      onClick={() => navigate(`/alunas/${row.student_id}/editar`)}
                      style={{ cursor: 'pointer' }}
                    >
                      {row.student_name}
                    </span>
                    {row.student_email ? (
                      <span className={orcStyles['student-email']}>{row.student_email}</span>
                    ) : (
                      <span className={orcStyles['email-empty']}>E-mail não informado</span>
                    )}
                  </div>

                  <div className={styles.tagsCell}>
                    {(row.project || shouldDisplayStudentClassificationBadge(row.classification) || row.is_reactivated || row.is_sensitive || row.is_pregnant || row.is_red_flag) ? (
                      <>
                        <ProjectBadge project={row.project} />
                        {shouldDisplayStudentClassificationBadge(row.classification) && (
                          <ClassificationBadge
                            label={row.classification.label}
                            backgroundColor={getStudentClassificationBadgeColors(row.classification).background}
                            textColor={getStudentClassificationBadgeColors(row.classification).text}
                          />
                        )}
                        <ReactivatedBadge visible={row.is_reactivated} />
                        <SensitiveStudentBadge visible={row.is_sensitive} reason={row.sensitive_reason} />
                        <PregnantStudentBadge visible={row.is_pregnant} pregnantUntil={row.pregnant_until} />
                        <RedFlagBadge visible={row.is_red_flag} />
                      </>
                    ) : (
                      <span className={orcStyles['email-empty']}>—</span>
                    )}
                  </div>

                  <div className={orcStyles['col-last-workout']}>
                    {row.joined_at ? (
                      <span className={orcStyles['last-workout-text']}>{formatDate(row.joined_at)}</span>
                    ) : (
                      <span className={orcStyles['last-workout-empty']}>—</span>
                    )}
                  </div>

                  <EarlyCheckinSuccessCell kind="day_8" status={row.day8_status} />

                  <EarlyCheckinSuccessCell kind="day_15" status={row.day15_status} />

                  <div className={styles.statusCell}>
                    <Badge variant={EARLY_CHECKIN_STAGE_VARIANTS[row.stage]} solid>
                      {EARLY_CHECKIN_STAGE_LABELS[row.stage]}
                    </Badge>
                  </div>
                </DataTable.Row>
              ))}
            </DataTable>

            <div className={styles.bottomRow}>
              <PageSizeSelect
                value={pageSize}
                options={pageSizeOptions}
                onChange={handlePageSizeChange}
              />
              <div className={styles.paginationCenter}>
                <Pagination
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  loading={loading}
                />
              </div>
            </div>
          </>
        )}
      </PageBody>
    </PageContainer>
  );
};

export default OrchestratorFup;
